import EmptyState from "@/components/EmptyState";
import Table, { MobileTableSortControl, TableHeading, buildActionColumn } from "@/components/Table";
import { buildTagColumn } from "@/components/Table/buildTagColumn";
import { renderEmptyCellState } from "@/components/Table/utils";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import {
  APPLICATION_TYPE_LABELS,
  STAGE_LABELS,
  UNASSIGNED_REVIEWER_LABEL,
  type ApplicationStage
} from "@/pages/people/membership-services/applications/types";
import { useRowHighlight } from "@/hooks/useRowHighlight";
import { useQuery } from "@apollo/client";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Path, useNavigate } from "@/router";
import { GET_PERSON_APPLICATION_HISTORY_QUERY, type PersonMembershipApplication } from "./queries";

const stageTagType: Record<ApplicationStage, "surface" | "information" | "warn" | "success" | "error"> = {
  draft: "surface",
  submitted: "surface",
  under_review: "information",
  changes_requested: "warn",
  approved: "success",
  denied: "error"
};

function buildColumns(
  setMenu: ReturnType<typeof useTableRowMenu>["setMenu"],
  onViewApplication: (applicationId: string) => void
): ColumnDef<PersonMembershipApplication, unknown>[] {
  return [
    {
      id: "type",
      header: "Type",
      accessorFn: (row) => row.applicationType,
      cell: ({ row }) => APPLICATION_TYPE_LABELS[row.original.applicationType] ?? row.original.applicationType,
      size: 180,
      meta: {
        sortType: "text",
        headerClassName: "hide-header-in-column"
      }
    },
    {
      id: "submitted",
      header: "Submitted",
      accessorFn: (row) => row.submittedAt,
      cell: ({ row }) =>
        row.original.submittedAt ? toFormat(row.original.submittedAt, "MMM dd, yyyy") : renderEmptyCellState(null),
      size: 130,
      meta: { sortType: "date" }
    },
    {
      id: "section",
      header: "Section",
      accessorFn: (row) => row.person.primarySection?.name,
      cell: ({ getValue }) => renderEmptyCellState(getValue()),
      size: 160,
      meta: { sortType: "text" }
    },
    {
      id: "reviewer",
      header: "Reviewer",
      accessorFn: (row) => row.reviewer?.name ?? UNASSIGNED_REVIEWER_LABEL,
      size: 180,
      meta: { sortType: "text" }
    },
    buildTagColumn<PersonMembershipApplication>({
      accessorKey: "status",
      header: "Stage",
      getTagLabel: (row) => STAGE_LABELS[row.status] ?? row.status,
      getTagType: (row) => stageTagType[row.status] ?? "surface",
      getTagStyle: () => "strong",
      size: 140,
      sortType: "text"
    }),
    buildActionColumn<PersonMembershipApplication>({
      getActions: (application) => [
        {
          text: "View Application",
          onClick: () => onViewApplication(application.id)
        }
      ],
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];
}

export default function MembershipApplications() {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });

  const { pgaId } = useParams();
  const navigate = useNavigate();
  const { Menu, setMenu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const [sortingState, setSortingState] = useState<SortingState>([{ id: "submitted", desc: true }]);

  const { data, loading, error } = useQuery(GET_PERSON_APPLICATION_HISTORY_QUERY, {
    variables: { pgaId: pgaId! },
    fetchPolicy: "cache-first",
    skip: !pgaId
  });

  const handleViewApplication = useCallback(
    (applicationId: string) => {
      navigate("/people/membership-services/applications/:applicationId", {
        params: { applicationId }
      });
    },
    [navigate]
  );

  const columns = useMemo(() => buildColumns(setMenu, handleViewApplication), [setMenu, handleViewApplication]);
  const applications = data?.person?.membershipApplications ?? [];
  const { highlightRowId, containerRef } = useRowHighlight({
    dataReady: !loading && applications.length > 0
  });

  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
      <Menu />
      <div ref={containerRef}>
        <TableHeading body="View path to membership applications processed in Member 360, including those for the Affiliate, Associate, and Member programs. Historical applications processed in CCMS are not included at this time." />
        <Table
          loading={loading}
          data={applications}
          columns={columns}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          getRowLink={(row) => ({
            to: `/people/membership-services/applications/${row.original.id}` as Path
          })}
          getRowClasses={
            highlightRowId ? (row) => (row.original.id === highlightRowId ? "row-highlight" : "") : undefined
          }
          renderControls={
            isCompactLayout && (loading || applications.length > 0)
              ? (_table, { onSortClick } = {}) => <MobileTableSortControl onSortClick={onSortClick} />
              : undefined
          }
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Application History"
              caption="This person does not have any Member 360-processed membership applications on record"
              iconName="description"
            />
          )}
          data-testid="membership-application-history-table"
        />
      </div>
    </>
  );
}
